import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Award, Truck, Sparkles, Link2Off } from "lucide-react";
import { cn } from "@/lib/utils";
import { useProducts, useProductCategories } from "@/hooks/use-products";
import { useShopMarket } from "@/lib/shop-market";
import { ProductCard } from "@/components/ProductCard";

/** Trust chip on the shop header — quiet, bordered, same pill language as the filters. */
function TrustChip({ icon: Icon, label }: { icon: typeof Award; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
      <Icon className="w-4 h-4 text-primary" />
      {label}
    </span>
  );
}

/**
 * The shop. Under /b2b/<key> (see ShopMarketProvider) the same page shows the
 * B2B market: its own products, prices and categories.
 */
export default function Shop() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { b2bKey } = useShopMarket();
  const { data: products, isLoading, isError } = useProducts(b2bKey);
  const { data: categories } = useProductCategories(b2bKey);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const copy = b2bKey ? "shop.b2b" : "shop";

  const filtered = useMemo(() => {
    const list = products || [];
    if (categoryFilter === "all") return list;
    return list.filter((p) => p.categoryId === categoryFilter);
  }, [products, categoryFilter]);

  // A wrong or replaced B2B key: say so instead of an empty shop.
  if (b2bKey && isError) {
    return (
      <div className="min-h-screen pt-24 pb-24 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="b2b-link-invalid">
          <Link2Off className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="text-lg font-semibold mb-2">{t("shop.b2b.invalidTitle")}</p>
          <p className="text-muted-foreground">{t("shop.b2b.invalidBody")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <p className="u-eyebrow text-secondary mb-3">{t(`${copy}.eyebrow`)}</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-display">{t(`${copy}.title`)}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">{t(`${copy}.subtitle`)}</p>
        </div>

        {/* trust strip — retail promises; the B2B market has its own terms */}
        {!b2bKey && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <TrustChip icon={Award} label={t("shop.trust.experience")} />
            <TrustChip icon={Truck} label={t("shop.trust.delivery")} />
            <TrustChip icon={Sparkles} label={t("shop.trust.sameProducts")} />
          </div>
        )}

        {/* category chips */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[{ id: "all", name: t("shop.allProducts") } as { id: string; name: string }]
            .concat(
              (categories || []).map((c) => ({
                id: c.id,
                name: isRtl ? c.nameAr : c.nameEn,
              })),
            )
            .map((option) => (
              <Badge
                key={option.id}
                variant={categoryFilter === option.id ? "default" : "outline"}
                className={cn(
                  "cursor-pointer rounded-full px-4 py-2 text-sm transition-all whitespace-nowrap",
                  categoryFilter === option.id && "bg-primary text-primary-foreground",
                )}
                onClick={() => setCategoryFilter(option.id)}
                data-testid={`shop-filter-${option.id}`}
              >
                {option.name}
              </Badge>
            ))}
        </div>

        {isLoading ? (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="rounded-3xl border border-border bg-card overflow-hidden">
                <div className="h-44 bg-muted animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-5 w-2/3 bg-muted animate-pulse rounded" />
                  <div className="h-4 w-full bg-muted animate-pulse rounded" />
                  <div className="h-8 w-28 bg-muted animate-pulse rounded-full" />
                </div>
              </li>
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground">{t("shop.empty")}</p>
        ) : (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
